import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useDocReader, useDocReaderDispatch } from '../store/docReaderContext';
import { getViewport } from './utils';
import { isNumber } from '../util';
import { RENDER_TYPE } from '../base';
import debounce from 'lodash/debounce';

const MIN_SCALE = 0.01;
const WHEEL_FACTOR = 0.002;
let initialDistance = 0; // 记录双指缩放时的初始距离
function getScaleByWidth(pdfPage: any, width: number): number {
    // 初始化时自动匹配最佳比例
    let scale = 1;
    const viewport = getViewport(pdfPage, 1);
    // toFixed 为4舍5入，为了防止入后变大，先减去0.05
    scale = Number((width / viewport.width - 0.005).toFixed(2));
    return scale;
}
export { getScaleByWidth };
export default function useDocReaderScale(containerHeight?: number) {
    const [state] = useDocReader();
    const dispatch = useDocReaderDispatch();
    const { pdf, pageNum, pdfContentRef, loading, renderType } = state;
    const getScale = (pdfPage: any) => {
        // 初始化时自动匹配最佳比例
        let { initScale } = state;
        const { scale } = state;
        let autoScale = scale;
        if (!scale) {
            const viewport = getViewport(pdfPage, 1);
            const containerWidth = pdfContentRef.current!.offsetWidth;
            // toFixed 为4舍5入，为了防止入后变大，先减去0.05
            let xScale = Number((containerWidth / viewport.width - 0.005).toFixed(2));
            xScale = Math.max(xScale, MIN_SCALE);
            const yScale = containerHeight
                ? Number(Math.max(containerHeight / viewport.height, MIN_SCALE).toFixed(2))
                : xScale;
            autoScale = Math.min(xScale, yScale);
            initScale = autoScale;
        }
        return { scale: autoScale, initScale };
    };
    const updateScaleOnWheel = useCallback((e: WheelEvent) => {
        if (!e.deltaY || !e.ctrlKey) {
            return;
        }
        e.preventDefault();

        // 缩放执行
        if (e.deltaY < 0) {
            dispatch({ type: 'zoomIn', delta: -WHEEL_FACTOR * e.deltaY });
        } else if (e.deltaY > 0) {
            dispatch({ type: 'zoomOut', delta: WHEEL_FACTOR * e.deltaY });
        }
    }, []);
    const updateScaleOnTouchMove = useCallback((e: TouchEvent) => {
        if (!e.touches || e.touches.length !== 2) {
            return;
        }

        e.preventDefault();
        const currentDistance = Math.sqrt(
            Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
                Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2),
        );
        if (initialDistance === 0) {
            initialDistance = currentDistance;
        }

        const delta = (currentDistance - initialDistance) * WHEEL_FACTOR;

        if (delta > 0) {
            dispatch({ type: 'zoomIn', delta: delta });
        } else if (delta < 0) {
            dispatch({ type: 'zoomOut', delta: -delta });
        }

        initialDistance = currentDistance;
    }, []);

    useEffect(() => {
        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches && e.touches.length === 2) {
                initialDistance = 0;
            }
        };

        const handleTouchEnd = () => {
            initialDistance = 0;
        };

        pdfContentRef.current?.addEventListener('wheel', updateScaleOnWheel);
        pdfContentRef.current?.addEventListener('touchmove', updateScaleOnTouchMove);
        pdfContentRef.current?.addEventListener('touchstart', handleTouchStart);
        pdfContentRef.current?.addEventListener('touchend', handleTouchEnd);

        return () => {
            pdfContentRef.current?.removeEventListener('wheel', updateScaleOnWheel);
            pdfContentRef.current?.removeEventListener('touchmove', updateScaleOnTouchMove);
            pdfContentRef.current?.removeEventListener('touchstart', handleTouchStart);
            pdfContentRef.current?.removeEventListener('touchend', handleTouchEnd);
        };
    }, [pdfContentRef.current]);
    // 使用Canvas 或图片渲染时，当scale变化时，需要重新渲染
    const firstScaleUpdate = useRef(true);
    const rerender = useCallback(
        debounce(() => {
            dispatch({ type: 'rerender' });
        }, 500),
        [],
    );
    useLayoutEffect(() => {
        if (state.scale <= 0) {
            return;
        }
        if (firstScaleUpdate.current) {
            firstScaleUpdate.current = false;
            return;
        }
        if (loading || renderType !== RENDER_TYPE.IMAGE) {
            return;
        }
        rerender();
    }, [state.scale]);
    useEffect(() => {
        if (!isNumber(pageNum.value) || !pdf.getPage || state.scale) {
            return;
        }
        pdf.getPage(pageNum.value).then((page: any) => {
            try {
                const { scale } = getScale(page);
                dispatch({ type: 'zoomSet', scale });
            } catch (e) {
                console.error('useDocReaderScale:pdf.getPage', e);
            }
        });

        return;
    }, [pdfContentRef.current, pdf, pageNum, state.scale]);
    useEffect(() => {
        pdfContentRef.current?.style.setProperty('--scale-factor', `${state.scale}`);
    }, [pdfContentRef.current, state.scale, pdf, pageNum]);
    return;
}
